/**
 * Per-node-type key templates for the docbody codec (docBodyCodec.js).
 *
 * Each template lists a node type's exported keys IN THE ORDER Lexical's
 * `exportJSON` emits them (lexical 0.46: an element's `children, direction,
 * format, indent, type, version` then its own keys; a text node's `detail,
 * format, mode, style, text, type, version` then its own keys). An entry is
 * `[key, default]` — dropped from storage while it equals the default — or
 * `[key]` — always stored, listed only so expansion restores the key order.
 *
 * Derived from real exports of every node LexicalRichEditor registers
 * (docBodyCodec.test.js builds them in a headless editor). When unsure about a
 * key, it has NO default: correctness over bytes. A type missing here is
 * stored verbatim (its children are still compacted).
 */

const KEEP = undefined;

// `direction`: lexical 0.46 no longer derives it while typing — it is null
// unless the content came in with a `dir` attribute (pasted / older rows).
const element = (...own) => [
  ['children', []],
  ['direction', null],
  ['format', ''],
  ['indent', 0],
  ['type', KEEP],
  ['version', 1],
  ...own,
];

const text = (...own) => [
  ['detail', 0],
  ['format', 0],
  ['mode', 'normal'],
  ['style', ''],
  ['text', KEEP],
  ['type', KEEP],
  ['version', 1],
  ...own,
];

const leaf = (...own) => [['type', KEEP], ['version', 1], ...own];

const LINK = [['rel', null], ['target', null], ['title', null], ['url', KEEP]];

const TEMPLATES = {
  paragraph: element(['textFormat', 0], ['textStyle', '']),
  heading: element(['tag', KEEP]),
  quote: element(),
  list: element(['listType', KEEP], ['start', 1], ['tag', KEEP]),
  listitem: element(['checked', KEEP], ['value', 1]),
  code: element(['language', KEEP]),
  link: element(...LINK),
  autolink: element(...LINK, ['isUnlinked', false]),
  table: element(['colWidths', KEEP], ['frozenColumnCount', KEEP], ['frozenRowCount', KEEP], ['rowStriping', KEEP]),
  tablerow: element(['height', KEEP]),
  tablecell: element(['backgroundColor', null], ['colSpan', 1], ['headerState', 0], ['rowSpan', 1], ['width', KEEP]),
  callout: element(['kind', 'info']),
  text: text(),
  'code-highlight': text(['highlightType', KEEP]),
  tab: [['detail', 2], ['format', 0], ['mode', 'normal'], ['style', ''], ['text', '\t'], ['type', KEEP], ['version', 1]],
  linebreak: leaf(),
  'doc-image': leaf(
    ['src', ''], ['pendingRef', ''], ['k', ''], ['enc', KEEP], ['name', KEEP], ['mime', KEEP], ['size', KEEP],
    ['w', KEEP], ['h', KEEP], ['alt', ''], ['width', null]
  ),
  'doc-video': leaf(['provider', 'youtube'], ['videoId', KEEP], ['start', 0]),
  'page-link': leaf(['docId', KEEP], ['scopeType', 'personal'], ['teamId', null], ['title', KEEP]),
};

/**
 * `type` -> `{ order: [key…], defaults: Map(key -> default) }`, or null for a
 * type with no template.
 */
const COMPILED = new Map(
  Object.entries(TEMPLATES).map(([type, entries]) => [
    type,
    {
      order: entries.map(([key]) => key),
      defaults: new Map(entries.filter(([, def]) => def !== KEEP).map(([key, def]) => [key, def])),
    },
  ])
);

export function templateFor(type) {
  return COMPILED.get(type) || null;
}

/**
 * The two commonest types are implied rather than stored: a compact node with
 * no `type` is a `text` node when it has `text`, else a `paragraph`.
 */
export const IMPLIED_TYPES = Object.freeze(['text', 'paragraph']);

export function impliedType(node) {
  return Object.prototype.hasOwnProperty.call(node, 'text') ? 'text' : 'paragraph';
}

/** Is `value` the template default `def`? (`[]` only for empty arrays.) */
export function isDefault(value, def) {
  if (Array.isArray(def)) return Array.isArray(value) && value.length === 0 && def.length === 0;
  return value === def;
}

/** A fresh copy of a default (never share the `[]` instance). */
export function defaultValue(def) {
  return Array.isArray(def) ? [] : def;
}
